import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TESTIDS } from "@/constants/testIds";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "converted", "archived"];

const STATUS_STYLES = {
  new: "border-[var(--td-gold)] text-[var(--td-gold)]",
  contacted: "border-blue-400 text-blue-300",
  converted: "border-emerald-400 text-emerald-300",
  archived: "border-[var(--td-border)] text-[var(--td-text-3)]",
};

export default function IntakeQueuePage() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    api.listIntakes().then(setItems).finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, contacted: 0, converted: 0, archived: 0 };
    for (const i of items) c[i.status] = (c[i.status] || 0) + 1;
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    if (activeTab === "all") return items;
    return items.filter((i) => i.status === activeTab);
  }, [items, activeTab]);

  const setStatus = async (id, status) => {
    // optimistic
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    try { await api.updateIntakeStatus(id, status); toast.success(`Marked ${status}`); }
    catch { toast.error("Failed"); api.listIntakes().then(setItems); }
  };

  const TABS = [
    { key: "all", label: "All" },
    { key: "new", label: t("iq_status_new") },
    { key: "contacted", label: t("iq_status_contacted") },
    { key: "converted", label: t("iq_status_converted") },
    { key: "archived", label: t("iq_status_archived") },
  ];

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12">
      <div className="mb-10">
        <div className="kicker mb-3">— Queue</div>
        <h1 className="font-serif text-4xl">{t("iq_title")}</h1>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors flex items-center gap-2 ${
              activeTab === tab.key
                ? "border-[var(--td-gold)] text-[var(--td-gold)]"
                : "border-[var(--td-border)] text-[var(--td-text-2)] hover:border-[var(--td-text-2)]"
            }`}
          >
            {tab.label}
            <span className="text-[var(--td-text-3)]">{counts[tab.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[var(--td-text-2)] font-mono text-xs">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-16 text-center text-[var(--td-text-2)]">
          <div className="font-serif text-xl">{t("iq_empty")}</div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="hairline-bottom">
                {[t("iq_col_when"), t("iq_col_company"), t("iq_col_contact"), t("iq_col_email"), t("iq_col_type"), t("iq_col_urgency"), t("iq_col_status"), t("iq_col_brief")].map((h) => (
                  <th key={h} className="text-left px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-2)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr key={i.id} className="hairline-bottom hover:bg-[var(--td-surface-2)] transition-colors">
                  <td className="px-5 py-4 font-mono text-xs text-[var(--td-text-3)] whitespace-nowrap">
                    {new Date(i.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-4 font-serif text-base">{i.company_name}</td>
                  <td className="px-5 py-4 text-[var(--td-text-2)]">{i.contact_name}</td>
                  <td className="px-5 py-4 font-mono text-xs">{i.email}</td>
                  <td className="px-5 py-4 font-mono text-xs text-[var(--td-text-2)]">{i.deal_type}</td>
                  <td className="px-5 py-4">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                      i.urgency === "urgent" ? "border-red-500 text-red-400" :
                      i.urgency === "high" ? "border-[var(--td-gold)] text-[var(--td-gold)]" :
                      "border-[var(--td-border)] text-[var(--td-text-2)]"
                    }`}>
                      {i.urgency}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={TESTIDS.intakeStatusSelect}
                      value={i.status}
                      onChange={(e) => setStatus(i.id, e.target.value)}
                      className={`td-input !py-1 !px-2 !text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLES[i.status] || ""}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{t(`iq_status_${s}`)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-4 text-[var(--td-text-2)] text-xs max-w-md truncate" title={i.brief}>{i.brief}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
